import React, { useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { Pagination, PaginationItem } from '@material-ui/lab';
import { TEACHERS_BULK_UPLOAD_WORK_UPLOAD_AND_QR_URL } from 'config/urls';
import {
  DEFAULT_FILTER_VALUE,
  FILTER_OPTIONS_MAP
} from 'constants/studentGoals';
import { useEmptyGoalsSubmissionsDetailGet, useStudentGoals } from 'sdk';

import { ReactComponent as AddIconBlue } from 'pages/Teachers/StudentDetail/assets/add-blue.svg';
import { ReactComponent as AddIconWhite } from 'pages/Teachers/StudentDetail/assets/add-white.svg';
import { ReactComponent as FlagIcon } from 'pages/Teachers/StudentDetail/assets/flag.svg';
import GoalRow from 'pages/Teachers/StudentDetail/components/GoalRow';
import { buildFilters } from 'utils/filters';
import { reverse } from 'utils/urls';

import styles from './styles.module.scss';

const GoalsTable = ({
  studentId,
  sectionId,
  goalName,
  filters,
  hasGoals,
  openGoalModal
}) => {
  const history = useHistory();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const currentPage = '1';
  const itemsPerPage = 5;
  const currentOffset = (currentPage - 1) * itemsPerPage;
  const [submissionPeriodFilter, setSubmissionPeriodFilter] =
    useState(DEFAULT_FILTER_VALUE);

  const params = useMemo(() => {
    return { ...filters, goal_name: goalName };
  }, [filters, goalName]);

  const {
    data: studentGoals,
    isLoading: isLoadingStudentGoals,
    refetch: refetchStudentDetails
  } = useStudentGoals({
    studentId,
    sectionId,
    filters: buildFilters(params),
    limit: itemsPerPage,
    offset: currentOffset
  });

  const totalCount = studentGoals?.count || 0;
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const goals = studentGoals?.results || [];

  const isEmpty = useMemo(
    () => !isLoadingStudentGoals && studentGoals.results.length === 0,
    [isLoadingStudentGoals, studentGoals]
  );

  const { data: submissionDetails } = useEmptyGoalsSubmissionsDetailGet({
    studentId,
    sectionId,
    period: FILTER_OPTIONS_MAP[submissionPeriodFilter],
    isEmpty: isEmpty,
    hasGoals: hasGoals
  });

  const isSubmissionDetailsEmpty = submissionDetails?.length === 0;

  const handlePageChange = (event, value) => {
    searchParams.set('section_page', value);
    history.push({
      pathname: location.pathname,
      search: searchParams.toString()
    });
  };

  const handleUploadWork = () => {
    history.push({
      pathname: reverse(TEACHERS_BULK_UPLOAD_WORK_UPLOAD_AND_QR_URL)
    });
  };

  return (
    <>
      {!hasGoals && isSubmissionDetailsEmpty ? (
        <div className={styles.emptyStateContainer}>
          <div className={styles.emptyStateIcon}>
            <FlagIcon />
          </div>
          <h3 className={styles.emptyStateMessage}>
            No goal or assignment data yet
          </h3>
          <p className={styles.emptyStateSubtext}>
            Add a goal or upload student work to start tracking the goal and
            data progress for this student
          </p>
          <div className={styles.actionsContainer}>
            <button className={styles.uploadButton} onClick={handleUploadWork}>
              <AddIconWhite /> UPLOAD WORK
            </button>
            <button
              className={styles.addGoalButton}
              onClick={() => openGoalModal()}
            >
              <AddIconBlue /> ADD GOAL
            </button>
          </div>
        </div>
      ) : (
        <div className={styles.tableContainer}>
          <div className={styles.tableHeader}>
            <div className={styles.colGoal}>Goal</div>
            <div className={styles.colDate}>By Date</div>
            <div className={styles.colProgress}>Progress</div>
          </div>
          {goals.length === 0 ? (
            !hasGoals && submissionDetails ? (
              <GoalRow
                title="No Goal added yet"
                description="This student has assignment data, so the chart below shows recent assignment accuracy"
                submissionDetails={submissionDetails}
                setSubmissionPeriodFilter={setSubmissionPeriodFilter}
                sectionId={sectionId}
                studentId={studentId}
                refetchStudentDetails={refetchStudentDetails}
              />
            ) : (
              <GoalRow
                title="Goal tracking pending student work"
                description="Create an assignment with matching standards to populate this goal's tracking data."
                setSubmissionPeriodFilter={setSubmissionPeriodFilter}
                sectionId={sectionId}
                studentId={studentId}
                refetchStudentDetails={refetchStudentDetails}
              />
            )
          ) : (
            goals.map((goal) => (
              <GoalRow
                key={goal.id}
                title={goal.name}
                description={goal.description}
                standards={goal.standards}
                date={goal.due_date}
                progress={goal.progress_percentage}
                targetAccuracy={goal.average_target_accuracy}
                goalId={goal.id}
                sectionId={sectionId}
                studentId={studentId}
                refetchStudentDetails={refetchStudentDetails}
              />
            ))
          )}
        </div>
      )}
      {totalPages > 1 && (
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          className={styles.pagination}
          renderItem={(item) => <PaginationItem {...item} disableRipple />}
        />
      )}
    </>
  );
};

export default GoalsTable;
